// @react
import { Dispatch, SetStateAction, useContext } from 'react'
import { useParams, useHistory } from 'react-router-dom'
// @common
import Helper from 'utils/Helper.class'
// @components
import { WorkFormValues } from 'modules/common/forms/types/work'
import { Direction, NavigationContext } from 'modules/common/context/navigation'
import { ProfileFormValues } from 'modules/common/forms/types/profile'
import { EducationFormValues } from 'modules/common/forms/types/education'
import { ContactFormValues } from 'modules/common/forms/types/contact'
import { CommunicationFormValues } from 'modules/common/forms/types/communication'
import { DeepMap, FieldError } from 'react-hook-form'

type PropsType = {
  setDestination: Dispatch<SetStateAction<string>>
  setDirection: Dispatch<SetStateAction<Direction>>
  setSlidingIn: Dispatch<SetStateAction<boolean>>
  setIsSubmitting: Dispatch<SetStateAction<boolean>>
  updateHandler?: (data: any) => Promise<boolean>
  isFormDirty: boolean
}

export enum SuccessAction {
  PREV = 'prev',
  EXIT = 'exit',
  NEXT = 'next',
}

type Inputs = any

const useNavigation = <T extends Inputs>({
  setDestination,
  setDirection,
  setSlidingIn,
  setIsSubmitting,
  updateHandler,
  isFormDirty,
}: PropsType) => {
  const navigation = useContext(NavigationContext)
  // @ts-ignore
  const { id: stringId } = useParams()
  const id = parseInt(stringId)
  const history = useHistory()

  const successHandler = (type: string): void => {
    switch (type) {
      case SuccessAction.PREV:
        navigation.setDirection(Direction.LEFT)
        setDestination(`/onboarding/step/${id - 1}`)
        setDirection(Direction.RIGHT)
        setSlidingIn(false)
        break
      case SuccessAction.NEXT:
        navigation.setDirection(Direction.RIGHT)
        setDestination(`/onboarding/step/${id + 1}`)
        setDirection(Direction.LEFT)
        setSlidingIn(false)
        break
      default:
      case SuccessAction.EXIT:
        history.push(Helper.getPathFromRouteKey('MYATD_DASHBOARD'))
        break
    }
  }

  /**
   *
   * @param data
   * @param errors
   */
  const handleSaveExit = async <
    T extends
      | WorkFormValues
      | ProfileFormValues
      | EducationFormValues
      | ContactFormValues
      | CommunicationFormValues
  >(
    data?: T,
    errors?: DeepMap<
      | WorkFormValues
      | ProfileFormValues
      | EducationFormValues
      | ContactFormValues
      | CommunicationFormValues,
      FieldError
    >,
  ) => {
    if (errors && isFormDirty) {
      return
    }

    if (isFormDirty) {
      setIsSubmitting(true)
      const res = updateHandler ? await updateHandler(data) : true
      setIsSubmitting(false)
      if (res) {
        successHandler(SuccessAction.EXIT)
      }
    } else {
      successHandler(SuccessAction.EXIT)
    }
  }

  /**
   *
   * @param data
   * @param errors
   */
  const handlePrevClick = async <
    T extends
      | WorkFormValues
      | ProfileFormValues
      | EducationFormValues
      | ContactFormValues
      | CommunicationFormValues
  >(
    data?: T,
    errors?: DeepMap<
      | WorkFormValues
      | ProfileFormValues
      | EducationFormValues
      | ContactFormValues
      | CommunicationFormValues,
      FieldError
    >,
  ) => {
    if (errors && isFormDirty) {
      return
    }

    if (isFormDirty) {
      setIsSubmitting(true)
      const res = updateHandler ? await updateHandler(data) : true
      setIsSubmitting(false)
      if (res) {
        successHandler(SuccessAction.PREV)
      }
    } else {
      successHandler(SuccessAction.PREV)
    }
  }

  /**
   *
   * @param data
   * @param errors
   */
  const handleNextClick = async <
    T extends
      | WorkFormValues
      | ProfileFormValues
      | EducationFormValues
      | ContactFormValues
      | CommunicationFormValues
  >(
    data?: T,
    errors?: DeepMap<
      | WorkFormValues
      | ProfileFormValues
      | EducationFormValues
      | ContactFormValues
      | CommunicationFormValues,
      FieldError
    >,
  ) => {
    if (errors && isFormDirty) {
      return
    }
    if (isFormDirty) {
      setIsSubmitting(true)
      const res = updateHandler ? await updateHandler(data) : true
      setIsSubmitting(false)
      if (res) {
        successHandler(SuccessAction.NEXT)
      }
    } else {
      successHandler(SuccessAction.NEXT)
    }
  }

  return {
    handleNextClick,
    handlePrevClick,
    handleSaveExit,
    successHandler,
  }
}

export default useNavigation
